import {
    PageSuggestion,
    CollectionSuggestion,
    CreatePageSuggestion,
    CreateCollectionSuggestion,
    TaskSuggestion,
    CreateTaskSuggestion,
} from '@/types';
import React from 'react';
import { SuggestionButton } from '../editor/Suggestions';
import { InputDropdown, InputDropdownProps, RenderItemProps } from './Dropdown';
import { PopOver } from './PopOver';

export type PagesCollectionsSuggestions =
    | PageSuggestion
    | CollectionSuggestion
    | TaskSuggestion
    | CreatePageSuggestion
    | CreateCollectionSuggestion
    | CreateTaskSuggestion;

// The In and Out types are needed to restrict the types of what can be sumbmitted based on
// -items passed down and the
// - items returned by the filter function
export type Props<In extends PagesCollectionsSuggestions, Out extends PagesCollectionsSuggestions> = {
    onSubmit: (data: {
        search: string;
        selectedItem: Out;
        event: React.SyntheticEvent<Element, Event> | Event;
    }) => void;
    getSuggestions(): In[];
    filterBySearch: (suggestions: In[], search: string) => Out[];
    isOpen: boolean;
    onClose: () => void;
    attachToRef: React.MutableRefObject<HTMLElement | null>;
    dropdownProps?: InputDropdownProps<In>['dropdownProps'];
    inputProps?: InputDropdownProps<In>['inputProps'];
    label: string;
};

function PagesCollectionsPopOver<In extends PagesCollectionsSuggestions, Out extends PagesCollectionsSuggestions>({
    isOpen,
    onClose,
    onSubmit,
    attachToRef,
    getSuggestions,
    filterBySearch: filterSuggestions,
    inputProps,
    dropdownProps,
    label,
}: Props<In, Out>) {
    return (
        <InputDropdown
            attachToRef={attachToRef}
            isOpen={isOpen}
            onClose={onClose}
            onSubmit={onSubmit}
            inputProps={inputProps}
            dropdownProps={dropdownProps}
            renderItems={({ search }) => {
                const suggestionsItems = filterSuggestions(getSuggestions(), search).map((item) => {
                    const render = ({ isSelected, setSelection, submit, index }: RenderItemProps) => {
                        return (
                            <React.Fragment key={index}>
                                {suggestionsItems.length > 1 && search.trim().length >= 0 && index === 0 && (
                                    <>
                                        <PopOver.Label>{label}</PopOver.Label>
                                    </>
                                )}
                                <SuggestionButton
                                    suggestion={item}
                                    selected={isSelected}
                                    onMouseEnter={setSelection}
                                    onClick={submit}
                                    showIcons={true}
                                >
                                    {item.title.trim().length > 0 ? item.title : 'Untitled'}
                                </SuggestionButton>
                            </React.Fragment>
                        );
                    };

                    return {
                        render,
                        item,
                    };
                });

                return suggestionsItems;
            }}
        />
    );
}

export function appendCreateCollectionSuggestion<A>(
    suggestions: A[],
    search: string,
): (A | CreateCollectionSuggestion)[] {
    if (search.trim().length === 0) {
        return suggestions;
    }

    return [...suggestions, { type: 'create', kind: 'collection', title: search }];
}

export function appendCreatePageSuggestion<A>(
    suggestions: A[],
    search: string,
    isTemplate = false,
): (A | CreatePageSuggestion)[] {
    if (search.trim().length === 0) {
        return suggestions;
    }

    return [...suggestions, { type: 'create', kind: 'page', title: search, isTemplate }];
}

export function appendCreateTaskSuggestion<A>(suggestions: A[], search: string): (A | CreateTaskSuggestion)[] {
    if (search.trim().length === 0) {
        return suggestions;
    }

    return [...suggestions, { type: 'create', kind: 'task', title: search }];
}

export default PagesCollectionsPopOver;
